import React, { useCallback, useEffect, useRef, useState } from 'react';
import { publicPost } from 'helpers/requests';
import { apiErrorMessage } from 'helpers/serverIssues';
import { buildPublicPortalPath } from '../publicPortalRoutes';

/**
 * Downloads the supplier's CBAM emissions report from the PUBLIC portal link.
 *
 * Rendered IN THE BROWSER by the same component the app uses —
 * `CbamStock/components/LotsEmissionsReportPDF` — so the visitor gets the same
 * document a logged-in user would generate, not a lookalike. There is no
 * Lambda, no S3, no presigned URL and nothing to poll: the backend returns
 * data, `pdf().toBlob()` does the rest.
 *
 *   POST /public/cbam-portal/:slug/report-data  { password }
 *        → { operator, installations, lots, cnGoodsByCode, years }
 *
 * The password travels in the body only — never a query string, never the URL —
 * because the portal gate is stateless and re-verifies on every request.
 *
 * One document covers every installation: the component groups its records by
 * `lot.installationId` and emits a section per installation, so a
 * multi-installation portal needs no zip and no merging.
 */

const URL_REVOKE_DELAY_MS = 5000;

const fetchReportData = (slug, password, surface) =>
  publicPost(`${buildPublicPortalPath(slug, { surface })}/report-data`, {
    password: password ?? null,
  });

export const slugifyName = (value) =>
  (value || 'supplier')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'supplier';

export default function usePortalReportPdf({ slug, password, surface = 'page', operatorName }) {
  const [status, setStatus] = useState('idle'); // idle | pending | error
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);
  const objectUrlRef = useRef(null);

  useEffect(
    () => () => {
      cancelledRef.current = true;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  const saveBlob = useCallback((blob, filename) => {
    const url = URL.createObjectURL(blob);
    const prev = objectUrlRef.current;
    objectUrlRef.current = url;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Revoke on a delay — revoking immediately can abort the download in some
    // browsers before they have read the blob.
    if (prev) setTimeout(() => URL.revokeObjectURL(prev), URL_REVOKE_DELAY_MS);
  }, []);

  const download = useCallback(async () => {
    if (!slug || status === 'pending') return;
    cancelledRef.current = false;
    setError(null);
    setStatus('pending');

    try {
      const res = await fetchReportData(slug, password, surface);
      const data = res.data ?? {};
      const lots = data.lots ?? [];
      if (lots.length === 0) throw new Error('There is nothing to report on yet.');

      // @react-pdf is the largest vendor chunk (1,652 kB raw / 556 kB gzip) and
      // this hook hangs off an EAGER public surface: PublicCatalogSharePage is
      // reached through _app.jsx's publicCatalogRoutes, which no amount of
      // route-level laziness in _routes.jsx touches. A static import here
      // therefore pinned the whole chunk into the entry graph for every visitor,
      // including ones who never ask for a PDF. Loaded on demand instead, at the
      // one moment it is actually needed.
      const [{ pdf }, reportModule] = await Promise.all([
        import('@react-pdf/renderer'),
        import('modules/CbamStock/components/LotsEmissionsReportPDF'),
      ]);
      // A plain binding, not `{ default: LotsEmissionsReportDoc }` destructuring:
      // lint-structure's JSX-scope check does not see names bound that way.
      const LotsEmissionsReportDoc = reportModule.default;

      const blob = await pdf(
        <LotsEmissionsReportDoc
          lots={lots}
          // Generic report: a portal visitor is not a named buyer, so the
          // document renders its "Generic Emissions Report" heading rather than
          // being addressed to someone.
          buyerName={null}
          years={data.years ?? []}
          cnGoodsByCode={data.cnGoodsByCode ?? {}}
          operator={data.operator ?? null}
          installations={data.installations ?? []}
          // No Clerk session on a public page, so no organisation logo to
          // inline; the document falls back to the platform mark.
          operatorLogoUrl={null}
        />,
      ).toBlob();

      if (cancelledRef.current) return;

      const base = slugifyName(data.operator?.name || operatorName);
      saveBlob(blob, `${base}-cbam-emissions-report.pdf`);
      setStatus('idle');
    } catch (err) {
      if (cancelledRef.current) return;
      setError(apiErrorMessage(err) || err?.message || 'Something went wrong.');
      setStatus('error');
    }
  }, [slug, password, surface, status, operatorName, saveBlob]);

  return {
    download,
    isPending: status === 'pending',
    // Kept so the modal's progress line has a stable shape; a client-side
    // render has no per-report stages to report.
    progress: null,
    error,
    reset: () => {
      setError(null);
      setStatus('idle');
    },
  };
}
